/**
 * Enhanced Country Detection for Production
 * Detects specific countries for proper template selection
 */

import { SPAIN_BIO_COUNTRIES } from "@/config/bioLinkConfig";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

/**
 * Detect specific country from timezone and browser language
 * Returns country code or null if not detectable
 * 
 * Priority:
 * 1. Test country (localStorage - development)
 * 2. Cloudflare Worker (`getServerTemplateConfig`: __TEMPLATE_CONFIG__ o cookies user_country) — ISO2 normalizado
 * 3. Timezone-based detection
 * 4. Browser language fallback
 */
export function detectCountryCode(): string | null {
  if (typeof window === "undefined") return null;

  // Check for test country first (development)
  const testCountry = localStorage.getItem("test_country");
  if (testCountry) {
    return testCountry;
  }

  const server = getServerTemplateConfig();
  if (server?.country && server.country !== "UNKNOWN") {
    return server.country;
  }

  // Fallback: Get timezone and language
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const browserLang =
    navigator.language ||
    (navigator as unknown as { userLanguage: string }).userLanguage;
  const langCountry = browserLang.split("-")[1]?.toUpperCase();

  // Timezone-based detection (most accurate)
  const timezoneCountryMap: Record<string, string> = {
    // Spain
    "Europe/Madrid": "ES",
    "Europe/Barcelona": "ES",
    "Atlantic/Canary": "ES",

    // Germany
    "Europe/Berlin": "DE",
    "Europe/Busingen": "DE",

    // France
    "Europe/Paris": "FR",

    // Italy
    "Europe/Rome": "IT",

    // Portugal
    "Europe/Lisbon": "PT",
    "Atlantic/Madeira": "PT",
    "Atlantic/Azores": "PT",

    // Netherlands
    "Europe/Amsterdam": "NL",

    // Belgium
    "Europe/Brussels": "BE",

    // Austria
    "Europe/Vienna": "AT",

    // Switzerland
    "Europe/Zurich": "CH",

    // United Kingdom
    "Europe/London": "GB",

    // Ireland
    "Europe/Dublin": "IE",

    // Nordic countries
    "Europe/Stockholm": "SE",
    "Europe/Oslo": "NO",
    "Europe/Copenhagen": "DK",
    "Europe/Helsinki": "FI",

    // Eastern Europe
    "Europe/Warsaw": "PL",
    "Europe/Prague": "CZ",
    "Europe/Athens": "GR",
    "Europe/Bucharest": "RO",
    "Europe/Budapest": "HU",
    "Europe/Bratislava": "SK",
    "Europe/Sofia": "BG",
    "Europe/Zagreb": "HR",
    "Europe/Ljubljana": "SI",
    "Europe/Vilnius": "LT",
    "Europe/Riga": "LV",
    "Europe/Tallinn": "EE",
    "Europe/Luxembourg": "LU",
    "Europe/Malta": "MT",
    "Europe/Nicosia": "CY",
    "Atlantic/Reykjavik": "IS",

    // Middle East
    "Asia/Dubai": "AE",
    "Asia/Riyadh": "SA",
    "Asia/Qatar": "QA",
    "Asia/Kuwait": "KW",
    "Asia/Bahrain": "BH",
    "Asia/Muscat": "OM",

    // US
    "America/New_York": "US",
    "America/Chicago": "US",
    "America/Denver": "US",
    "America/Los_Angeles": "US",
    "America/Phoenix": "US",
    "America/Anchorage": "US",
    "America/Adak": "US",
    "Pacific/Honolulu": "US",

    // Mexico
    "America/Mexico_City": "MX",
    "America/Cancun": "MX",
    "America/Monterrey": "MX",
    "America/Tijuana": "MX",

    // Brazil
    "America/Sao_Paulo": "BR",
    "America/Manaus": "BR",
    "America/Fortaleza": "BR",
    "America/Rio_Branco": "BR",
    "America/Recife": "BR",

    // Chile
    "America/Santiago": "CL",
    "Pacific/Easter": "CL",

    // Paraguay
    "America/Asuncion": "PY",

    // Argentina
    "America/Argentina/Buenos_Aires": "AR",
    "America/Argentina/Cordoba": "AR",
    "America/Argentina/Mendoza": "AR",
    "America/Argentina/Tucuman": "AR",
    "America/Argentina/Salta": "AR",
    "America/Argentina/Jujuy": "AR",
    "America/Argentina/San_Juan": "AR",
    "America/Argentina/San_Luis": "AR",
    "America/Argentina/Catamarca": "AR",
    "America/Argentina/La_Rioja": "AR",
    "America/Argentina/Ushuaia": "AR",

    // Other LATAM countries
    "America/Bogota": "CO",
    "America/Lima": "PE",
    "America/Caracas": "VE",
    "America/Guayaquil": "EC",
    "America/Montevideo": "UY",
    "America/La_Paz": "BO",
    "America/Costa_Rica": "CR",
    "America/Panama": "PA",
    "America/Guatemala": "GT",
    "America/Tegucigalpa": "HN",
    "America/El_Salvador": "SV",
    "America/Managua": "NI",
    "America/Santo_Domingo": "DO",
    "America/Havana": "CU",
    "America/Puerto_Rico": "PR",
  };

  // Direct timezone match
  const countryFromTimezone = timezoneCountryMap[timezone];
  if (countryFromTimezone) {
    return countryFromTimezone;
  }

  // Partial timezone matching for complex names
  if (timezone.includes("Argentina")) return "AR";
  if (timezone.includes("Mexico")) return "MX";
  if (timezone.includes("Santiago")) return "CL";
  if (timezone.includes("Sao_Paulo") || timezone.includes("Brazil"))
    return "BR";
  if (timezone.includes("Asuncion")) return "PY";
  if (timezone.includes("Madrid") || timezone.includes("Barcelona"))
    return "ES";
  if (timezone.includes("Berlin") || timezone.includes("Germany")) return "DE";
  if (timezone.includes("Paris") || timezone.includes("France")) return "FR";

  // Language-based fallback
  if (langCountry && isValidCountryCode(langCountry)) {
    return langCountry;
  }

  return null;
}

/**
 * Check if country code is valid
 * Includes all countries supported for video/pricing configuration
 */
function isValidCountryCode(code: string): boolean {
  const validCodes = [
    // Americas
    "US",
    "MX", "AR", "CO", "PE", "CL", "VE", "EC", "BO", "PY", "UY", "BR", // LATAM
    "CR", "PA", "DO", "GT", "HN", "NI", "SV", "CU", "PR", // Central America & Caribbean
    
    // Europe
    "ES", "DE", "FR", "IT", "PT", "NL", "BE", "AT", "CH", "GB", "IE",
    "SE", "NO", "DK", "FI", "PL", "CZ", "GR", "RO", "HU", "SK",
    "BG", "HR", "SI", "LT", "LV", "EE", "LU", "MT", "CY", "IS",
    
    // Middle East
    "AE", "SA", "QA", "KW", "BH", "OM",
  ];
  return validCodes.includes(code);
}

/**
 * Determine if country should use Spain Bio template
 */
export function shouldCountryUseSpainBio(countryCode: string | null): boolean {
  if (!countryCode) return false;
  return SPAIN_BIO_COUNTRIES.includes(
    countryCode as (typeof SPAIN_BIO_COUNTRIES)[number],
  );
}

/**
 * Get template type for detected country
 */
export function getTemplateForDetectedCountry():
  | "BaseTemplate"
  | "BioLinkTemplate" {
  const countryCode = detectCountryCode();

  if (!countryCode) {
    // No country detected - default to BaseTemplate
    return "BaseTemplate";
  }

  // Spain Bio countries use BioLinkTemplate
  if (shouldCountryUseSpainBio(countryCode)) {
    return "BioLinkTemplate";
  }

  // All other countries use BaseTemplate
  return "BaseTemplate";
}

/**
 * Get country detection info for debugging
 */
export function getCountryDetectionInfo() {
  const countryCode = detectCountryCode();
  const timezone =
    typeof window !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : null;
  const browserLang = typeof window !== "undefined" ? navigator.language : null;
  const template = getTemplateForDetectedCountry();
  const isSpainBio = countryCode
    ? shouldCountryUseSpainBio(countryCode)
    : false;

  return {
    countryCode,
    timezone,
    browserLang,
    template,
    isSpainBio,
    testMode:
      typeof window !== "undefined"
        ? !!localStorage.getItem("test_country")
        : false,
  };
}

export default {
  detectCountryCode,
  shouldCountryUseSpainBio,
  getTemplateForDetectedCountry,
  getCountryDetectionInfo,
};
